'use client';
import React, { useState, useEffect, useCallback } from 'react';

const ERROR_MESSAGES: Record<string, { title: string; description: string; retryable: boolean }> = {
  strava_denied: {
    title: 'Autorizzazione negata',
    description: 'Hai annullato l\'accesso a Strava. Riprova e accetta le autorizzazioni richieste.',
    retryable: true,
  },
  strava_token: {
    title: 'Errore di autenticazione',
    description: 'Impossibile ottenere il token da Strava. Riprova tra qualche secondo.',
    retryable: true,
  },
  strava_signup: {
    title: 'Errore nella creazione account',
    description: 'Si è verificato un problema durante la creazione del tuo account. Riprova o contatta il supporto.',
    retryable: true,
  },
  strava_error: {
    title: 'Errore imprevisto',
    description: 'Si è verificato un errore durante il login con Strava. Riprova tra qualche secondo.',
    retryable: true,
  },
  network_error: {
    title: 'Errore di rete',
    description: 'Controlla la tua connessione internet e riprova.',
    retryable: true,
  },
};

export default function StravaAuthButton({ mode }: { mode: 'login' | 'signup' }) {
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const err = params.get('error');
      if (err && ERROR_MESSAGES[err]) {
        setErrorKey(err);
        // Clean the URL without reload
        const url = new URL(window.location.href);
        url.searchParams.delete('error');
        window.history.replaceState({}, '', url.toString());
      }
    }
  }, []);

  const handleClick = useCallback(() => {
    setLoading(true);
    setErrorKey(null);
    try {
      window.location.href = '/api/auth/strava';
    } catch {
      setLoading(false);
      setErrorKey('network_error');
    }
  }, []);

  const handleRetry = useCallback(() => {
    setRetryCount((c) => c + 1);
    handleClick();
  }, [handleClick]);

  const errorInfo = errorKey ? ERROR_MESSAGES[errorKey] : null;
  const MAX_RETRIES = 3;
  const canRetry = errorInfo?.retryable && retryCount < MAX_RETRIES;

  return (
    <div className="w-full">
      <button
        onClick={errorInfo ? handleRetry : handleClick}
        disabled={loading || (!!errorInfo && !canRetry)}
        className="strava-btn w-full flex items-center justify-center gap-3 px-5 py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
        aria-busy={loading}
        aria-label={loading ? 'Connessione in corso...' : mode === 'login' ? 'Accedi con Strava' : 'Registrati con Strava'}
      >
        {loading ? (
          <>
            <svg className="animate-spin w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Connessione in corso...
          </>
        ) : errorInfo && canRetry ? (
          <>
            <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Riprova con Strava
            {retryCount > 0 && <span className="text-xs opacity-75">({retryCount}/{MAX_RETRIES})</span>}
          </>
        ) : (
          <>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
            </svg>
            {mode === 'login' ? 'Accedi con Strava' : 'Registrati con Strava'}
          </>
        )}
      </button>

      {errorInfo && (
        <div
          role="alert"
          className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 flex gap-2.5"
        >
          <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-red-700">{errorInfo.title}</p>
            <p className="text-xs text-red-600 mt-0.5">{errorInfo.description}</p>
            {!canRetry && retryCount >= MAX_RETRIES && (
              <p className="text-xs text-red-500 mt-1 font-medium">
                Troppi tentativi falliti. Ricarica la pagina o contatta il supporto.
              </p>
            )}
          </div>
          {canRetry && (
            <button
              onClick={handleRetry}
              disabled={loading}
              className="text-xs text-red-600 font-semibold underline underline-offset-2 hover:text-red-800 flex-shrink-0 disabled:opacity-50"
            >
              Riprova
            </button>
          )}
        </div>
      )}
    </div>
  );
}